import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Pagination,
  CircularProgress,
  TextField,

  Collapse,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import styles from './TrainingProgressPage.module.scss';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useTrainings } from '../../api/hooks';
import { useTrainingEnrollmentProgress } from '../../api/hooks/useTrainingProgress';
import { useTrainingStructure } from '../../api/hooks/useTrainingStructure';
import { TrainingDto, transformToTableData } from '../../api/types/dtos/training';
import TrainingProgressTable from './TrainingProgressTable';
import GroupUpdateModal from './components/GroupUpdateModal';
import { useHasPrivilege } from '../../hooks/usePermissions';
import { PRIVILEGES } from '../../api/types/dtos';

const TrainingProgressPage: React.FC = () => {
  const [selectedTraining, setSelectedTraining] = useState<TrainingDto | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  
  // Search filters
  const [searchAdminName, setSearchAdminName] = useState('');
  const [searchRegion, setSearchRegion] = useState('');
  const [searchPhone, setSearchPhone] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Debounced search filters
  const [debouncedAdminName, setDebouncedAdminName] = useState('');
  const [debouncedRegion, setDebouncedRegion] = useState('');
  const [debouncedPhone, setDebouncedPhone] = useState('');

  const { selectedOrganization, loading: organizationLoading } = useOrganization();

  const { hasPrivilege: canEdit } = useHasPrivilege(PRIVILEGES.UPDATE_TRAINING_CONTENT_PROGRESS);
  const { hasPrivilege: canCreate } = useHasPrivilege(PRIVILEGES.CREATE_TRAINING_CONTENT_PROGRESS);

  // Fetch trainings - hooks must be called before any conditional returns
  const { 
    data: trainings = [], 
    isLoading: trainingsLoading,
    error: trainingsError 
  } = useTrainings(
    selectedOrganization?.id || '',
    { isActive: true }
  );

  // Fetch enrollment progress with pagination and filters
  const {
    data: enrollmentProgressResponse,
    isLoading: progressLoading,
    refetch: refetchProgress,
  } = useTrainingEnrollmentProgress(
    selectedOrganization?.id || '',
    selectedTraining?.id || '',
    {
      page,
      size: pageSize,
      sortBy: 'organizationName',
      sortDirection: 'ASC',
      isActive: true,
      adminUserName: debouncedAdminName || undefined,
      regionName: debouncedRegion || undefined,
      phone: debouncedPhone || undefined,
    }
  );

  // Fetch complete training structure (modules, topics, contents)
  const {
    data: trainingStructure,
    isLoading: structureLoading,
  } = useTrainingStructure(
    selectedOrganization?.id || '',
    selectedTraining?.id || ''
  );

  // Auto-select first training
  useEffect(() => {
    if (trainings.length > 0 && !selectedTraining) {
      setSelectedTraining(trainings[0]);
    }
  }, [trainings, selectedTraining]);

  // Debounce search filters (1 second delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedAdminName(searchAdminName);
    }, 1000);

    return () => clearTimeout(timer);
  }, [searchAdminName]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedRegion(searchRegion);
    }, 1000);

    return () => clearTimeout(timer);
  }, [searchRegion]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedPhone(searchPhone);
    }, 1000);

    return () => clearTimeout(timer);
  }, [searchPhone]);

  // Reset to first page when debounced filters change
  useEffect(() => {
    setPage(0);
  }, [debouncedAdminName, debouncedRegion, debouncedPhone]);

  // Extract data - must be before conditional returns
  const modules = trainingStructure?.modules || [];
  const allTopics = trainingStructure?.topics || [];
  const allContents = trainingStructure?.contents || [];

  // Transform data for table - must be before conditional returns
  const tableData = useMemo(() => {
    if (!selectedTraining || !enrollmentProgressResponse || !modules.length) {
      return null;
    }

    return transformToTableData(
      selectedTraining.id,
      selectedTraining.name,
      enrollmentProgressResponse,
      modules,
      allTopics,
      allContents
    );
  }, [selectedTraining, enrollmentProgressResponse, modules, allTopics, allContents]);

  // Organization validation - after all hooks
  if (organizationLoading) {
    return (
      <Container maxWidth="xl" className={styles.container}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!selectedOrganization?.id) {
    return (
      <Container maxWidth="xl" className={styles.container}>
        <Alert severity="error" sx={{ mt: 3 }}>
          No se ha seleccionado una organización. Por favor, seleccione una organización para continuar.
        </Alert>
      </Container>
    );
  }

  const handleTrainingChange = (trainingId: string) => {
    const training = trainings.find((t) => t.id === trainingId);
    setSelectedTraining(training || null);
    setPage(0); // Reset to first page
    // Clear search filters when changing training
    setSearchAdminName('');
    setSearchRegion('');
    setSearchPhone('');
    setDebouncedAdminName('');
    setDebouncedRegion('');
    setDebouncedPhone('');
  };

  const handleClearFilters = () => {
    setSearchAdminName('');
    setSearchRegion('');
    setSearchPhone('');
    setDebouncedAdminName('');
    setDebouncedRegion('');
    setDebouncedPhone('');
    setPage(0);
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value - 1); // MUI Pagination is 1-indexed
  };

  const handlePageSizeChange = (event: any) => {
    setPageSize(event.target.value);
    setPage(0); // Reset to first page
  };

  const handleRefetch = () => {
    refetchProgress();
  };

  const handleGroupUpdateSuccess = () => {
    refetchProgress();
  };

  const isLoading = trainingsLoading || progressLoading || structureLoading;

  return (
    <Container maxWidth="xl" className={styles.container}>
      <Box className={styles.header}>
        <Box>
          <Typography variant="h4" component="h1">
            Training Progress
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Monitor and manage training progress for all enrolled organizations
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setGroupModalOpen(true)}
          disabled={!selectedTraining || !(canEdit || canCreate)}
        >
          Group Update
        </Button>
      </Box>

      <Box className={styles.content}>
        {/* Training Selector and Controls */}
        <Box className={styles.controls}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <FormControl sx={{ minWidth: 300 }}>
              <InputLabel>Select Training</InputLabel>
              <Select
                value={selectedTraining?.id || ''}
                onChange={(e) => handleTrainingChange(e.target.value)}
                label="Select Training"
                disabled={trainingsLoading}
              >
                {trainings.map((training) => (
                  <MenuItem key={training.id} value={training.id}>
                    {training.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedTraining && (
              <Button
                variant="outlined"
                startIcon={<FilterListIcon />}
                onClick={() => setShowFilters(!showFilters)}
              >
                {showFilters ? 'Hide Filters' : 'Show Filters'}
              </Button>
            )}
          </Box>

          {selectedTraining && tableData && (
            <Box className={styles.pagination}>
              <FormControl sx={{ minWidth: 120 }}>
                <InputLabel>Page Size</InputLabel>
                <Select
                  value={pageSize}
                  onChange={handlePageSizeChange}
                  label="Page Size"
                  size="small"
                >
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                  <MenuItem value={200}>200</MenuItem>
                </Select>
              </FormControl>

              <Typography variant="body2" color="text.secondary">
                Showing {page * pageSize + 1}-
                {Math.min((page + 1) * pageSize, tableData.totalElements)} of{' '}
                {tableData.totalElements} organizations
              </Typography>
            </Box>
          )}
        </Box>

        {/* Search Filters */}
        {selectedTraining && (
          <Collapse in={showFilters}>
            <Box className={styles.filtersContainer}>
              <Box className={styles.filters}>
                <TextField
                  label="Admin Name"
                  placeholder="Search by admin name..."
                  value={searchAdminName}
                  onChange={(e) => setSearchAdminName(e.target.value)}
                  size="small"
                  sx={{ minWidth: 200 }}
                  helperText="Search applies after 1 second"
                />
                <TextField
                  label="Region"
                  placeholder="Search by region..."
                  value={searchRegion}
                  onChange={(e) => setSearchRegion(e.target.value)}
                  size="small"
                  sx={{ minWidth: 200 }}
                  helperText="Search applies after 1 second"
                />
                <TextField
                  label="Phone"
                  placeholder="Search by phone..."
                  value={searchPhone}
                  onChange={(e) => setSearchPhone(e.target.value)}
                  size="small"
                  sx={{ minWidth: 200 }}
                  helperText="Search applies after 1 second"
                />
                {(searchAdminName || searchRegion || searchPhone) && (
                  <Button
                    variant="outlined"
                    startIcon={<ClearIcon />}
                    onClick={handleClearFilters}
                  >
                    Clear
                  </Button>
                )}
              </Box>
            </Box>
          </Collapse>
        )}

        {/* Loading State */}
        {isLoading && selectedTraining && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Table View */}
        {!isLoading && selectedTraining && tableData && (
          <>
            <TrainingProgressTable
              data={tableData}
              loading={progressLoading}
              onRefetch={handleRefetch}
              canEdit={canEdit}
              canCreate={canCreate}
            />

            {/* Pagination Controls */}
            {tableData.totalPages > 1 && (
              <Box className={styles.paginationContainer}>
                <Pagination
                  count={tableData.totalPages}
                  page={page + 1}
                  onChange={handlePageChange}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}
          </>
        )}

        {/* Error State */}
        {trainingsError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Error loading trainings. Please try refreshing the page.
          </Alert>
        )}

        {/* No Training Selected */}
        {!selectedTraining && !trainingsLoading && !trainingsError && (
          <Alert severity="info">
            Select a training to view progress for enrolled organizations
          </Alert>
        )}

        {/* No Enrollments */}
        {!isLoading &&
          selectedTraining &&
          tableData &&
          tableData.rows.length === 0 && (
            <Alert severity="info">
              No organizations are enrolled in this training yet
            </Alert>
          )}
      </Box>

      {/* Group Update Modal */}
      <GroupUpdateModal
        open={groupModalOpen}
        onClose={() => setGroupModalOpen(false)}
        onSuccess={handleGroupUpdateSuccess}
      />
    </Container>
  );
};

export default TrainingProgressPage;
